/** @jsxImportSource ../simboluri-jsx */
import { useEffect, useRef } from "react";
import { FemDecuplare, FemResult } from "../tipuri";
import Info from "../Info";
import { numar, t, useT, useTxn } from "../../i18n";
import { Focus } from "../legaturi";
import TitluVerificare, { useCatalogVerificari } from "../Verificare";

// ---------------------------------------------------------------------------
// Secțiunea „Verificări" din CAPUL pasului — sumarul cu
// contorul în stilul indicatorului Pasului 6 (vs-bar). Tabelul complet al
// verificărilor modale exista deja, dar stătea îngropat sub canvasul 3D și
// nimeni nu-l vedea; aici e verdictul dintr-o privire: cele 4 verificări
// modale ale gate-ului, criteriul de 90 % masă cumulată, asimetria
// flexibilității (informativă, nu intră în contor) și verdictul gate.
// Detaliile complete rămân jos, în «Verificarea modală a modelului».
// ---------------------------------------------------------------------------

type RandVerificareFem = {
  id: string;
  nume: string;
  valoare: string;
  prag: string;
  ok: boolean | null;
  /** ratată la mică distanță: se arată «de revizuit», nu picată */
  review?: boolean;
  formula?: string;
};

/** Verdictul unui rând al gate-ului modal: trece / de revizuit / pică. */
function VerdictFem({ ok, review, nota }:
                    { ok: boolean | null | undefined; review?: boolean;
                      nota?: string | null }) {
  const t = useT();
  if (ok == null)
    return <span className="sg-muted">{nota ?? t("struct.fem.vf_nedeterminat")}</span>;
  if (ok) return <span className="fem-ok">{t("struct.fem.vf_trece")}</span>;
  if (review) return <span className="st-review">{t("struct.fem.vf_review")}</span>;
  return <span className="st-bad">{t("struct.fem.vf_pica")}</span>;
}

/** Gate-ul modal a picat NUMAI prin separări «de revizuit» (10–15 %)? Atunci
 *  omul vede «de revizuit», deși solverul încă țintește 15 %. */
function gateDoarReview(d: FemDecuplare | null | undefined): boolean {
  if (!d || d.ok !== false) return false;
  const picate = (d.verificari ?? []).filter((x) => x.ok === false);
  return picate.length > 0 && picate.every((x) => x.review)
    && (d.motive ?? []).length === picate.length;
}

export function VerificariFem({ r, focus }: { r: FemResult; focus?: Focus }) {
  useT();
  const txn = useTxn();
  const v = r.verificare_modala;
  const d = v?.decuplare;
  const cutie = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (focus?.ref)
      cutie.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [focus?.n, focus?.ref]);
  const catalog = useCatalogVerificari();

  if (!v || v.eroare) {
    return (
      <div className="vs-bar vs-bad">
        <div className="vs-head">
          <span className="vs-total">
            <strong>{t("struct.fem.vf_titlu")}</strong>
          </span>
        </div>
        <div className="st-warn">
          • {v?.eroare ?? t("struct.fem.vf_nerulat")}
        </div>
      </div>
    );
  }

  // rezultat salvat de o versiune veche: gate-ul nu emitea încă lista
  // `verificari` — se spune EXPLICIT, nu se ascunde secțiunea tăcut
  const rezultatVechi = !d || !(d.verificari ?? []).length;

  const randuri: RandVerificareFem[] = (d?.verificari ?? []).map((x) => ({
    id: x.id,
    nume: x.nume,
    valoare:
      x.valoare_pct != null ? `${numar(x.valoare_pct)} %` : "—",
    prag: `≥ ${x.prag_pct} %`,
    ok: x.ok,
    review: !!x.review,
    formula: x.formula ?? x.nota,
  }));
  if (v.criteriu_90_ok != null) {
    randuri.push({
      id: "masa_90",
      nume: t("struct.fem.vf_masa90"),
      valoare: t("struct.fem.vf_masa90_val", {
        x: v.masa_cumulata_x_pct ?? "—", y: v.masa_cumulata_y_pct ?? "—" }),
      prag: t("struct.fem.vf_masa90_prag"),
      ok: v.criteriu_90_ok,
      formula: t("struct.fem.vf_masa90_formula", {
        x: v.masa_cumulata_x_pct ?? "—", y: v.masa_cumulata_y_pct ?? "—",
        moduri: v.nr_moduri ?? "—" }),
    });
  }

  const numarate = randuri.filter((x) => x.ok != null);
  const trec = numarate.filter((x) => x.ok === true).length;
  const pica = numarate.filter((x) => x.ok === false && !x.review).length;
  const deRevizuit = numarate.filter((x) => x.ok === false && x.review).length;
  const nedeterminate = randuri.length - numarate.length;
  const doarReview = gateDoarReview(d);
  const toateTrec = pica === 0 && (d?.ok !== false || doarReview);

  return (
    <div className={`vs-bar ${!toateTrec ? "vs-bad" : deRevizuit ? "vs-warn" : "vs-ok"}`}
         ref={cutie}>
      <div className="vs-head">
        <span className="vs-total">
          {txn("struct.fem.vf_numar#n", numarate.length)}
        </span>
        <span className="vs-chip vs-chip-ok">
          {t("struct.fem.vf_trec", { n: trec })}
        </span>
        {deRevizuit > 0 && (
          <span className="vs-chip vs-chip-warn">
            {t("struct.fem.vf_de_revizuit", { n: deRevizuit })}
          </span>
        )}
        <span className={`vs-chip ${pica ? "vs-chip-bad" : "vs-chip-mut"}`}>
          {t("struct.fem.vf_nu_trec", { n: pica })}
        </span>
        {nedeterminate > 0 && (
          <span className="vs-chip vs-chip-mut">
            {t("struct.fem.vf_nedeterminate", { n: nedeterminate })}
          </span>
        )}
        <Info
          titlu={t("struct.fem.vf_ind_titlu")}
          ce={t("struct.fem.vf_ind_ce")}
          deCe={t("struct.fem.vf_ind_dece")}
        />
      </div>

      {rezultatVechi && (
        <div className="vs-verdict sol-avert">
          {t("struct.fem.vf_vechi")}
        </div>
      )}

      {randuri.length > 0 && (
        <table className="sg-table vs-lista">
          <thead>
            <tr>
              <th>{t("struct.fem.vf_col_verificarea")}</th>
              <th>{t("struct.fem.vf_col_valoarea")}</th>
              <th>{t("struct.fem.vf_col_pragul")}</th>
              <th>{t("struct.fem.vf_col_verdict")}</th>
              <th>{t("struct.fem.vf_col_formula")}</th>
            </tr>
          </thead>
          <tbody>
            {randuri.map((x) => (
              <tr key={x.id}>
                {/* explicația la hover, din ACELAȘI catalog ca pe paginile
                    Pașilor 6/10/13/14/15 și ca în tabelul Pasului 19: numele
                    specific al rândului rămâne vizibil, categoria dă textul */}
                <td>
                  <TitluVerificare catalog={catalog} pas="model_fem"
                                   nume={x.nume} text={x.nume}
                                   aratNumele={false} aratPasul={false} />
                </td>
                <td className="sg-mono">{x.valoare}</td>
                <td className="sg-mono">{x.prag}</td>
                <td><VerdictFem ok={x.ok} review={x.review} /></td>
                <td className="sg-muted">{x.formula ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {d && (
        <div className="vs-verdict">
          {d.ok ? (
            <span className="fem-ok">{t("struct.fem.vf_gate_ok")}</span>
          ) : (
            <>
              {doarReview
                ? <span className="st-review">{t("struct.fem.vf_gate_review")}</span>
                : <span className="st-bad">{t("struct.fem.vf_gate_nu")}</span>}
              {(d.motive ?? []).length > 0 && <> — {(d.motive ?? []).join("; ")}</>}
              {d.directie_recomandata && (
                <div className="sg-muted">
                  {t("struct.fem.vf_directie",
                     { directie: d.directie_recomandata })}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {v.asimetrie_flexibilitate && (
        <div className="vs-verdict sg-muted">
          {t("struct.fem.vf_asimetrie")}{" "}
          <span className="sg-mono">{v.asimetrie_flexibilitate}</span>{" "}
          {t("struct.fem.vf_asimetrie_sub")}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tabelul de verificare modală
// ---------------------------------------------------------------------------

export function VerificareModala({ r }: { r: FemResult }) {
  const catalog = useCatalogVerificari();
  const v = r.verificare_modala;
  const shell = r.parametri.varianta_model === "shell";
  const cmp = r.sanity?.comparatie_lean;
  return (
    <div className="st-findings">
      <h3>
        {t("struct.fem.vm_titlu")}
        <Info
          titlu={t("struct.fem.vm_info_titlu")}
          ce={t("struct.fem.vm_info_ce")}
          deCe={t("struct.fem.vm_info_dece")}
          parametri={[
            { simbol: "T", nume: t("struct.fem.vm_t_nume"),
              sursa: t("struct.fem.vm_t_sursa") },
            { simbol: "mX/mY/mRZ", nume: t("struct.fem.vm_m_nume"),
              sursa: t("struct.fem.vm_m_sursa") },
          ]}
        />
      </h3>
      {v?.eroare ? (
        <div className="st-warn">• {v.eroare}</div>
      ) : v?.moduri ? (
        <>
          <table className="sg-table fem-modes">
            <thead>
              <tr>
                <th>{t("struct.fem.col_mod")}</th>
                <th>T (s)</th>
                <th>f (Hz)</th>
                <th title={t("struct.fem.vm_col_masa_x_titlu")}>
                  {t("struct.fem.col_masa_x")}</th>
                <th title={t("struct.fem.vm_col_masa_y_titlu")}>
                  {t("struct.fem.col_masa_y")}</th>
                {shell && <th title={t("struct.fem.vm_col_masa_z_titlu")}>
                  {t("struct.fem.vm_col_masa_z")}</th>}
                <th title={t("struct.fem.vm_col_torsiune_titlu")}>
                  {t("struct.fem.col_torsiune")}</th>
                {shell && (
                  <th title={t("struct.fem.vm_col_dist_titlu")}>
                    {t("struct.fem.vm_col_dist")}
                  </th>
                )}
                <th>{t("struct.fem.col_cum_x")}</th>
                <th>{t("struct.fem.col_cum_y")}</th>
                {shell && <th>{t("struct.fem.vm_col_tip")}</th>}
                {shell && (
                  <th title={t("struct.fem.vm_col_puritate_titlu")}>
                    {t("struct.fem.vm_col_puritate")}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {v.moduri.map((m) => (
                <tr key={m.mod}>
                  <td className="sg-mono">{m.mod}</td>
                  <td className="sg-mono">{m.T_s.toFixed(4)}</td>
                  <td className="sg-mono">{m.f_hz.toFixed(2)}</td>
                  <td className="sg-mono">{m.masa_x_pct}%</td>
                  <td className="sg-mono">{m.masa_y_pct}%</td>
                  {shell && <td className="sg-mono">{m.masa_z_pct ?? 0}%</td>}
                  <td className="sg-mono">{m.masa_rz_pct}%</td>
                  {shell && (
                    <td className="sg-mono"
                        title={t("struct.fem.vm_dist_celula")}>
                      {m.dist_x_pct != null
                        ? `${Math.round(m.dist_x_pct)} / ${Math.round(m.dist_y_pct ?? 0)} / ${Math.round(m.dist_rz_pct ?? 0)}`
                        : "—"}
                    </td>
                  )}
                  <td className="sg-mono">{m.cumulat_x_pct}%</td>
                  <td className="sg-mono">{m.cumulat_y_pct}%</td>
                  {shell && <td style={{ textAlign: "left" }}>{m.tip}</td>}
                  {shell && (
                    <td
                      className={
                        m.puritate != null && m.puritate < 0.8 && Math.max(m.masa_x_pct, m.masa_y_pct) >= 5
                          ? "sg-mono st-warn"
                          : "sg-mono"
                      }
                    >
                      {m.puritate != null ? m.puritate.toFixed(2) : "—"}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          <div className={v.criteriu_90_ok ? "fem-ok" : "st-warn"}>
            {v.criteriu_90_ok ? "✓" : "•"}{" "}
            {t("struct.fem.vm_cumulat", {
              x: v.masa_cumulata_x_pct ?? "—",
              y: v.masa_cumulata_y_pct ?? "—",
              verdict: t(v.criteriu_90_ok
                ? "struct.fem.vm_indeplinit" : "struct.fem.vm_neindeplinit") })}
            {v.nr_moduri_extrase != null && v.nr_moduri_min_cerut != null && (
              <div className="sg-muted" style={{ fontWeight: "normal" }}>
                {t("struct.fem.vm_moduri", {
                  n: v.nr_moduri ?? "—", extrase: String(v.nr_moduri_extrase ?? "—"),
                  minim: String(v.nr_moduri_min_cerut ?? "—") })}
                {v.meff_la_nr_moduri_min &&
                  (v.nr_moduri ?? 0) > v.nr_moduri_min_cerut
                  && t("struct.fem.vm_moduri_extra", {
                    minim: String(v.nr_moduri_min_cerut ?? "—"),
                    m0: String(v.meff_la_nr_moduri_min["0"] ?? "—"),
                    m90: String(v.meff_la_nr_moduri_min["90"] ?? "—") })}
              </div>
            )}
          </div>
          {v.decuplare && (
            <div className={v.decuplare.ok ? "fem-ok"
                             : gateDoarReview(v.decuplare) ? "st-review"
                             : "st-bad"}>
              {v.decuplare.ok ? "✓" : gateDoarReview(v.decuplare) ? "◐" : "✗"}
              {" "}{t("struct.fem.moduri_decuplate_gate")}{" "}
              {v.decuplare.ok ? (
                <>
                  {t("struct.fem.decuplare_ok", {
                    sep: v.decuplare.prag_separare_t_pct,
                    dir: v.decuplare.prag_directie_pct,
                  })}
                </>
              ) : (
                <>{t("struct.fem.vm_neindeplinit")} — {(v.decuplare.motive ?? []).join("; ")}</>
              )}
              {/* cele PATRU verificări numite, fiecare
                  cu verdict propriu — aceleași care intră, prioritizate, în
                  lista de verificări a Pasului 19 */}
              {(v.decuplare.verificari ?? []).length > 0 && (
                <table
                  className="sg-table"
                  style={{ marginTop: 8, fontWeight: "normal" }}
                >
                  <thead>
                    <tr>
                      <th>{t("struct.fem.vf_col_verificarea")}</th>
                      <th>{t("struct.fem.vf_col_valoarea")}</th>
                      <th>{t("struct.fem.vf_col_pragul")}</th>
                      <th>{t("struct.fem.vf_col_verdict")}</th>
                      <th>{t("struct.fem.vf_col_formula")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(v.decuplare.verificari ?? []).map((x) => (
                      <tr key={x.id}>
                        <td>
                          <TitluVerificare catalog={catalog} pas="model_fem"
                                           nume={x.nume} text={x.nume}
                                           aratNumele={false}
                                           aratPasul={false} />
                        </td>
                        <td className="sg-mono">
                          {x.valoare_pct != null
                            ? `${x.valoare_pct.toLocaleString("ro-RO")} %`
                            : "—"}
                        </td>
                        <td className="sg-mono">≥ {x.prag_pct} %</td>
                        <td>
                          <VerdictFem ok={x.ok} review={x.review}
                                      nota={x.nota} />
                        </td>
                        <td className="sg-muted">{x.formula ?? x.nota ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {!v.decuplare.ok && (
                <div className="sg-muted" style={{ fontWeight: "normal" }}>
                  {t("struct.fem.vm_pas19", {
                    directie: v.decuplare.directie_recomandata
                      ?? t("struct.fem.vm_directie_deficitara") })}
                </div>
              )}
              {v.decuplare.nota && (
                <div className="sg-muted" style={{ fontWeight: "normal" }}>
                  {v.decuplare.nota}
                </div>
              )}
            </div>
          )}
          {v.avertisment && <div className="st-warn">{v.avertisment}</div>}
          {shell && cmp && !cmp.eroare && (
            <div className="sg-muted fem-note">
              <strong>{t("struct.fem.vm_comparatie")}</strong>
              {t("struct.fem.vm_comparatie_sub", {
                t1s: String(cmp.T1_shell_s ?? "—"), t1l: String(cmp.T1_lean_s ?? "—"),
                semn: cmp.delta_pct! > 0 ? "+" : "", delta: String(cmp.delta_pct ?? "—"),
                masa: String(r.masa_totala_t ?? "—"),
                masaLean: String(cmp.masa_lean_t ?? "—") })}
              {cmp.arie_lean_m2 != null
                ? t("struct.fem.vm_cmp_placa", {
                    shell: r.sanity?.arie_poligon_m2 ?? "—",
                    lean: String(cmp.arie_lean_m2 ?? "—") })
                : t("struct.fem.vm_cmp_mesh",
                    { mesh: r.sanity?.acoperire_mesh_pct ?? "—" })}
              {cmp.nota_arie && <div className="st-warn">{cmp.nota_arie}</div>}
              {cmp.nota && <div>{cmp.nota}</div>}
            </div>
          )}
          <div className="sg-muted fem-note">{v.nota_moduri}</div>
        </>
      ) : (
        <div className="sg-muted">{t("struct.fem.vm_nerulat")}</div>
      )}
    </div>
  );
}
